import type React from "react";
import { useId } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { fmtHours, fmtInt, fmtPct } from "@/lib/format";
import { BRAND } from "@/lib/colors";
import { useChartTheme } from "@/lib/chart";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useMonthlyTrends, type TrendPoint } from "@/features/periods/usePeriods";

function pctDelta(latest: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((latest - previous) / previous) * 100;
}

function buildTiles(latest: TrendPoint, previous: TrendPoint): KPITile[] {
  function delta(latestVal: number, prevVal: number, higherIsBetter: boolean) {
    const pct = pctDelta(latestVal, prevVal);
    return pct == null ? null : { pct, higherIsBetter };
  }

  return [
    {
      label: "Total Sessions",
      value: fmtInt(latest.total_sessions),
      delta: delta(latest.total_sessions, previous.total_sessions, true),
    },
    {
      label: "Productive Hours",
      value: fmtHours(latest.productive_hours),
      delta: delta(latest.productive_hours, previous.productive_hours, true),
    },
    {
      label: "Extended Session Rate",
      value: fmtPct(latest.extended_session_rate),
      delta: delta(latest.extended_session_rate, previous.extended_session_rate, false),
    },
    {
      label: "Fleet Idle Rate",
      value: fmtPct(latest.fleet_idle_rate_pct),
      delta: delta(latest.fleet_idle_rate_pct, previous.fleet_idle_rate_pct, false),
    },
  ];
}

interface TrendChartProps {
  title: string;
  dataKey: keyof Pick<
    TrendPoint,
    "extended_session_rate" | "productive_hours" | "fleet_idle_rate_pct"
  >;
  stroke: string;
  yUnit: "pct" | "hours";
  trends: TrendPoint[];
}

function TrendChart({ title, dataKey, stroke, yUnit, trends }: TrendChartProps): React.JSX.Element {
  const chart = useChartTheme();
  const titleId = useId();
  return (
    <div className="card-duress p-5">
      <p id={titleId} className="font-head font-semibold text-sm text-foreground mb-4">{title}</p>
      <div role="img" aria-labelledby={titleId}>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={trends} margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
            stroke={chart.grid}
          />
          <YAxis
            tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
            stroke={chart.grid}
            tickFormatter={(v: number) => (yUnit === "pct" ? `${v.toFixed(0)}%` : `${v.toFixed(0)}h`)}
          />
          <Tooltip
            contentStyle={{
              fontFamily: "Inter",
              fontSize: 12,
              borderRadius: 8,
              background: chart.tooltipBg,
              border: `1px solid ${chart.tooltipBorder}`,
              color: chart.tooltipText,
            }}
            formatter={(v) => {
              const n = typeof v === "number" ? v : Number(v);
              return yUnit === "pct" ? fmtPct(n) : fmtHours(n);
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            name={title}
            stroke={stroke}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Pure presentational component ────────────────────────────────────────────

export interface MonthlyTrendsProps {
  trends: TrendPoint[];
}

export function MonthlyTrends({ trends }: MonthlyTrendsProps): React.JSX.Element {
  if (trends.length === 0) {
    return (
      <div data-trends-state="no-periods">
        <EmptyState icon={TrendingUp} body="Upload a reporting period to see trends." />
      </div>
    );
  }

  if (trends.length === 1) {
    return (
      <div data-trends-state="single-period">
        <EmptyState
          icon={TrendingUp}
          body="Monthly Trends needs at least 2 periods. Upload another period to see change over time."
        />
      </div>
    );
  }

  const latest = trends[trends.length - 1];
  const previous = trends[trends.length - 2];
  if (!latest || !previous) {
    // Unreachable: trends.length >= 2 was checked above. Required by noUncheckedIndexedAccess.
    return <></>;
  }
  const tiles = buildTiles(latest, previous);

  return (
    <div className="space-y-6" data-trends-state="populated">
      <KPIGrid tiles={tiles} />

      <div className="space-y-[10px]">
        <TrendChart
          title="Extended Session Rate Over Time"
          dataKey="extended_session_rate"
          stroke={BRAND.orange}
          yUnit="pct"
          trends={trends}
        />
        <TrendChart
          title="Productive Hours Over Time"
          dataKey="productive_hours"
          stroke={BRAND.blue}
          yUnit="hours"
          trends={trends}
        />
        <TrendChart
          title="Fleet Idle Rate Over Time"
          dataKey="fleet_idle_rate_pct"
          stroke={BRAND.red}
          yUnit="pct"
          trends={trends}
        />
      </div>

      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          Each point on these charts is one uploaded reporting period. The x-axis is ordered by
          (year, month) so historical periods always read left-to-right. The delta on each KPI
          tile compares the latest period to the immediately preceding period (and is omitted if
          the previous period was zero). Periods missing the optional Emergency, Check-in, or
          Team Alert CSVs still contribute to all three trend lines — these come from the
          always-present usage methodology.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ──────────────────────────────────────────

export function MonthlyTrendsTab({ clientId }: { clientId: string }): React.JSX.Element {
  const { trends, isLoading, error, refetch } = useMonthlyTrends(clientId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error) {
    return <ErrorCard message="Failed to load trend data." onRetry={refetch} />;
  }

  return <MonthlyTrends trends={trends} />;
}
